import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import Modal from '../ui/Modal'
import { api } from '../../api/client'
import { createDebug } from '../../utils/debugLog'
import type { FeedbackContextNode } from '../canvas-v2/nodes/shared/useNodeActions'
import { trackInteraction } from '../../analytics/interactionAnalytics'

const log = createDebug('dw:user-feedback:submit')

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  /** Required — feedback persists to the project-scoped DB endpoint. */
  projectId: string
  /**
   * Nodes the report is about (canvas node right-click menu → Send Feedback).
   * Their ids and types are appended to the stored feedback text.
   */
  contextNodes?: FeedbackContextNode[]
}

/**
 * The feedback text as stored: the user's description, then the nodes it is
 * about (ids + types only, never node content) so triage can find them.
 */
export function buildFeedbackText(description: string, contextNodes: readonly FeedbackContextNode[] = []): string {
  const text = description.trim()
  if (contextNodes.length === 0) return text
  const nodes = contextNodes.map((node) => `- ${node.id} (${node.type})`).join('\n')
  return `${text}\n\n---\nSelected nodes (${contextNodes.length}):\n${nodes}`
}

type ModalState = 'form' | 'submitting' | 'success' | 'error'

// DB feedback categories (the modal never sends 'other'). Mapped to a
// GitHub label server-side (feature -> feature_request).
type FeedbackCategory = 'bug' | 'feature' | 'question'

const CATEGORY_OPTIONS: { value: FeedbackCategory; labelKey: string; descriptionKey: string }[] = [
  { value: 'bug', labelKey: 'feedbackModal.bugReport', descriptionKey: 'feedbackModal.bugReportDescription' },
  { value: 'feature', labelKey: 'feedbackModal.featureRequest', descriptionKey: 'feedbackModal.featureRequestDescription' },
  { value: 'question', labelKey: 'feedbackModal.question', descriptionKey: 'feedbackModal.questionDescription' },
]

export function FeedbackModal({ isOpen, onClose, projectId, contextNodes = [] }: FeedbackModalProps) {
  const [state, setState] = useState<ModalState>('form')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<FeedbackCategory>('bug')
  const [errorMessage, setErrorMessage] = useState('')
  const { t } = useTranslation('workspace')

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setState('form')
      setTitle('')
      setDescription('')
      setCategory('bug')
      setErrorMessage('')
    }
  }, [isOpen])

  const canSubmit = title.trim().length > 0 && description.trim().length > 0

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return

    setState('submitting')
    setErrorMessage('')
    trackInteraction('canvas_feedback_submitted', { project_id: projectId || undefined, category })
    log('submitting feedback category=%s projectId=%s context_nodes=%d', category, projectId, contextNodes.length)

    try {
      // DB-first: success means the report is persisted. The GitHub-issue mirror
      // runs best-effort server-side and its failure never reaches this catch.
      const result = await api.submitWorkspaceFeedback(projectId, {
        feedback: buildFeedbackText(description, contextNodes),
        title: title.trim(),
        category,
        browser_info: {
          user_agent: navigator.userAgent,
          url: window.location.href,
        },
      })
      setState('success')
      log('feedback submitted id=%s', result.id)
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Something went wrong'
      setErrorMessage(err instanceof Error ? err.message : t('feedbackModal.somethingWentWrong'))
      setState('error')
      console.error('[Feedback] Submission failed:', rawMessage, err)
      log('feedback submission failed: %s', rawMessage)
    }
  }, [canSubmit, title, description, category, projectId, contextNodes, t])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('feedbackModal.sendFeedback')} size="sm">
      {state === 'form' && (
        <div className="space-y-4" data-testid="feedback-form">
          {contextNodes.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="feedback-context-nodes">
              {t('feedbackModal.aboutNodes', { count: contextNodes.length })}
            </p>
          )}
          {/* Category selection */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">
              {t('feedbackModal.category')}
            </label>
            <div className="flex gap-2">
              {CATEGORY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCategory(opt.value)}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors cursor-pointer border ${
                    category === opt.value
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'bg-surface-2 border-transparent text-muted-foreground hover:text-foreground hover:bg-surface-2/80'
                  }`}
                  title={t(opt.descriptionKey)}
                  data-testid={`feedback-category-${opt.value}`}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="feedback-title" className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('feedbackModal.titleLabel')}
            </label>
            <input
              id="feedback-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('feedbackModal.titlePlaceholder')}
              maxLength={200}
              className="w-full px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="feedback-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="feedback-description" className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('feedbackModal.descriptionLabel')}
            </label>
            <textarea
              id="feedback-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('feedbackModal.descriptionPlaceholder')}
              rows={5}
              maxLength={5000}
              className="w-full px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              data-testid="feedback-description-input"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="feedback-submit-button"
            >
              {t('feedbackModal.submitFeedback')}
            </button>
          </div>
        </div>
      )}

      {state === 'submitting' && (
        <div className="flex flex-col items-center justify-center py-8 gap-3" data-testid="feedback-submitting">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">{t('feedbackModal.submittingFeedback')}</p>
        </div>
      )}

      {state === 'success' && (
        <div className="flex flex-col items-center justify-center py-8 gap-3" data-testid="feedback-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          <p className="text-sm font-medium text-foreground">{t('feedbackModal.feedbackSubmitted')}</p>
        </div>
      )}

      {state === 'error' && (
        <div className="flex flex-col items-center justify-center py-8 gap-3" data-testid="feedback-error">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-sm font-medium text-foreground">{t('feedbackModal.failedToSubmit')}</p>
          <p className="text-xs font-medium text-muted-foreground text-center max-w-xs">{errorMessage}</p>
          <button
            type="button"
            onClick={() => setState('form')}
            className="mt-2 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer bg-surface-2 text-foreground hover:bg-surface-2/80"
            data-testid="feedback-retry-button"
          >
            {t('feedbackModal.tryAgain')}
          </button>
        </div>
      )}
    </Modal>
  )
}
